// ============================================================================
// Entity form registry
// ============================================================================
// Central definition of the editable form fields per table, in the shape the
// shared DetailDialog understands. Used by the AI agent's draft/confirm flow so
// a proposed add/edit renders as the SAME rich form (typed inputs, uploads, FKs,
// enums) an admin would use on the table's own page. Arabic-first labels.
// ============================================================================
import { arabicColumnLabel } from './columnLabels';

export type DraftFieldType =
  | 'text' | 'email' | 'number' | 'date' | 'textarea' | 'richtext' | 'select'
  | 'boolean' | 'multiselect' | 'currency' | 'image' | 'file';

export interface DraftField {
  name: string;
  label: string;
  type?: DraftFieldType;
  required?: boolean;
  width?: 'full' | 'half' | 'third';
  options?: Array<{ value: any; label: string }>;
  foreignKey?: { table: string; valueField: string; labelField: string };
  bucket?: string;
  accept?: string;
}

const titleCase = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const opt = (value: string, ar: string, en: string, isRTL: boolean) => ({
  value,
  label: isRTL ? ar : en,
});

// Returns the field list for an entity, or null if the agent should fall back
// to auto-generating text fields from the proposed keys.
export const getEntityDraftFields = (entity: string, isRTL: boolean): DraftField[] | null => {
  const L = (col: string) => (isRTL ? arabicColumnLabel(col) : titleCase(col));
  const f = (name: string, type: DraftFieldType = 'text', extra: Partial<DraftField> = {}): DraftField => ({
    name, label: L(name), type, ...extra,
  });

  switch (entity) {
    case 'news':
      return [
        f('cover_image_url', 'image', { bucket: 'gallery', width: 'full' }),
        f('title', 'text', { required: true }),
        f('body', 'richtext', { required: true, width: 'full' }),
        f('date', 'date'),
        f('source_url', 'text', { width: 'full' }),
      ];
    case 'events':
      return [
        f('title', 'text', { required: true }),
        f('start_date', 'date'),
        f('end_date', 'date'),
        f('details', 'textarea', { width: 'full' }),
      ];
    case 'trainings':
      return [
        f('title', 'text', { required: true }),
        f('date', 'date'),
        f('location', 'text'),
        f('instructor_name', 'text'),
        f('cost', 'number'),
        f('cost_currency', 'text'),
        f('registration_deadline', 'date'),
        f('announced_date', 'date'),
        f('status', 'select', {
          options: [
            opt('upcoming', 'قادم', 'Upcoming', isRTL),
            opt('ongoing', 'جارٍ', 'Ongoing', isRTL),
            opt('completed', 'مكتمل', 'Completed', isRTL),
            opt('cancelled', 'ملغى', 'Cancelled', isRTL),
          ],
        }),
        f('source', 'text'),
        f('source_url', 'text'),
        f('is_public', 'boolean'),
        f('needs_review', 'boolean'),
        f('data_notes', 'textarea', { width: 'full' }),
        f('description', 'richtext', { width: 'full' }),
        f('flyer_url', 'image', { bucket: 'gallery', width: 'full' }),
      ];
    case 'board_meetings':
      return [
        f('meeting_no', 'text', { required: true }),
        f('date', 'date'),
        f('time', 'text'),
        f('location', 'text', { width: 'half' }),
        f('attendees_count', 'number'),
        f('decisions_text', 'richtext', { width: 'full' }),
        f('source_url', 'text', { width: 'full' }),
      ];
    case 'board_attachments':
      return [
        { name: 'meeting_id', label: L('meeting_id'), required: true, width: 'half',
          foreignKey: { table: 'board_meetings', valueField: 'id', labelField: 'meeting_no,date' } },
        f('filename', 'text', { width: 'half' }),
        f('file_url', 'file', { bucket: 'board-minutes', accept: 'application/pdf', width: 'full' }),
      ];
    case 'photo_albums':
      return [
        f('name', 'text', { required: true }),
        f('cover_image_url', 'image', { bucket: 'gallery', width: 'full' }),
        f('source_url', 'text', { width: 'full' }),
        f('source', 'text'),
        f('is_public', 'boolean'),
      ];
    case 'gallery_photos':
      return [
        { name: 'album_id', label: L('album_id'), required: true, width: 'half',
          foreignKey: { table: 'photo_albums', valueField: 'id', labelField: 'name' } },
        f('image_url', 'image', { bucket: 'gallery', required: true, width: 'full' }),
        f('thumb_url', 'image', { bucket: 'gallery', width: 'full' }),
        f('source_url', 'text', { width: 'full' }),
        f('source', 'text'),
        f('is_public', 'boolean'),
      ];
    case 'branches':
      return [
        f('name', 'text', { required: true, width: 'half' }),
        f('phones', 'text', { width: 'half' }),
        f('details', 'textarea', { width: 'full' }),
      ];
    case 'related_links':
      return [
        f('label', 'text', { required: true, width: 'half' }),
        f('url', 'text', { required: true, width: 'half' }),
      ];
    case 'info_pages':
      return [
        f('page', 'text', { required: true, width: 'half' }),
        f('source_url', 'text', { width: 'half' }),
        f('content', 'richtext', { width: 'full' }),
      ];
    case 'tenders':
      return [
        f('tender_name', 'text', { required: true }),
        f('reference_no', 'text'),
        f('authority_dept', 'text'),
        f('tender_kind', 'text'),
        f('type', 'select', {
          options: [
            opt('public', 'عامة', 'Public', isRTL),
            opt('private', 'خاصة', 'Private', isRTL),
            opt('international', 'دولية', 'International', isRTL),
          ],
        }),
        f('area', 'text'),
        f('date', 'date'),
        f('currency', 'currency'),
        f('financier_name', 'text'),
        f('owner_name', 'text'),
        f('email', 'email'),
        f('phone_number', 'text'),
        f('telephone_number', 'text'),
        f('docs_price', 'number'),
        f('docs_currency', 'text'),
        f('docs_available_from', 'text'),
        f('site_visit', 'text'),
        f('inquiries_deadline', 'text'),
        f('submission_deadline', 'date'),
        f('submission_time', 'text'),
        f('opening_datetime', 'text'),
        f('bid_validity_days', 'number'),
        f('contact_fax', 'text'),
        f('website', 'text'),
        f('submission_address', 'text', { width: 'full' }),
        f('is_public', 'boolean'),
        f('needs_review', 'boolean'),
        f('ocr_notes', 'textarea', { width: 'full' }),
        f('link', 'text', { width: 'full' }),
        f('attachment', 'file', { bucket: 'tender-scans', accept: 'application/pdf,image/*', width: 'full' }),
      ];
    case 'tender_attachments':
      return [
        { name: 'tender_id', label: L('tender_id'), required: true, width: 'full',
          foreignKey: { table: 'tenders', valueField: 'id', labelField: 'tender_name,reference_no' } },
        f('filename', 'text', { width: 'full' }),
        f('bulletin_date', 'date'),
        f('file_url', 'image', { bucket: 'tender-scans', width: 'full' }),
      ];
    case 'messages':
      return [
        f('title', 'text', { required: true }),
        { name: 'company_id', label: L('company_id'),
          foreignKey: { table: 'companies', valueField: 'id', labelField: 'name,pcu_id' } },
        f('body', 'richtext', { width: 'full' }),
      ];
    case 'outward_letters':
      return [
        f('title', 'text', { required: true }),
        f('internal_id', 'text'),
        f('date', 'date'),
        f('delivery_method', 'select', {
          options: [
            opt('email', 'بريد إلكتروني', 'Email', isRTL),
            opt('fax', 'فاكس', 'Fax', isRTL),
            opt('hand_delivery', 'تسليم يدوي', 'Hand delivery', isRTL),
            opt('post', 'بريد', 'Post', isRTL),
          ],
        }),
        f('body', 'richtext', { width: 'full' }),
        f('attachment', 'file', { bucket: 'documents', width: 'full' }),
      ];
    case 'documents':
      return [
        f('title', 'text', { required: true }),
        f('category', 'select', {
          options: [
            opt('forms', 'نماذج', 'Forms', isRTL),
            opt('regulations', 'أنظمة', 'Regulations', isRTL),
            opt('guidelines', 'إرشادات', 'Guidelines', isRTL),
            opt('reports', 'تقارير', 'Reports', isRTL),
            opt('announcements', 'إعلانات', 'Announcements', isRTL),
            opt('certificates', 'شهادات', 'Certificates', isRTL),
            opt('manuals', 'أدلة', 'Manuals', isRTL),
            opt('other', 'أخرى', 'Other', isRTL),
          ],
        }),
        f('description', 'textarea', { width: 'full' }),
        f('file_path', 'file', { bucket: 'documents', width: 'full' }),
      ];
    case 'classifications':
      return [
        f('category', 'text'),
        f('field', 'text'),
        f('specialization', 'text'),
      ];
    case 'document_templates':
      return [
        f('name', 'text', { required: true }),
        f('is_default', 'boolean'),
        f('header_text', 'text', { width: 'half' }),
        f('footer_text', 'text', { width: 'half' }),
        f('primary_color', 'text'),
        f('columns', 'text', { width: 'half' }),   // comma-separated columns to show in exports
        f('max_rows', 'number'),                    // cap rows in the PDF
        f('description', 'textarea', { width: 'full' }),
      ];
    case 'branches_fallback':
      return null;
    default:
      return null;
  }
};

// Build fallback fields from the proposed keys when no registry entry exists.
export const autoDraftFields = (fields: Record<string, any>, isRTL: boolean): DraftField[] =>
  Object.keys(fields)
    .filter((k) => !['id', 'created_at', 'updated_at'].includes(k))
    .map((k) => ({
      name: k,
      label: isRTL ? arabicColumnLabel(k) : titleCase(k),
      type: (k.includes('date') ? 'date' : k.includes('email') ? 'email' : 'text') as DraftFieldType,
      width: 'full' as const,
    }));
